import { useEffect } from "react";
import { focusManager } from "@tanstack/react-query";

/**
 * Configures React Query's focus manager to refetch stale queries on both
 * window focus and visibility change events.
 *
 * By default, TanStack Query v5 only listens for `visibilitychange` (tab
 * switches). This hook adds a `focus` listener so that switching back from
 * another application also triggers refetches of stale queries.
 */
export function useWindowFocusRefetch() {
  useEffect(() => {
    focusManager.setEventListener((handleFocus) => {
      const onFocus = () => handleFocus(true);
      const onBlur = () => handleFocus(false);
      const onVisibilityChange = () => handleFocus(document.visibilityState === "visible");

      window.addEventListener("focus", onFocus);
      window.addEventListener("blur", onBlur);
      document.addEventListener("visibilitychange", onVisibilityChange);

      return () => {
        window.removeEventListener("focus", onFocus);
        window.removeEventListener("blur", onBlur);
        document.removeEventListener("visibilitychange", onVisibilityChange);
      };
    });

    return () => {
      // Restore default TanStack Query v5 focus manager (visibilitychange only)
      focusManager.setEventListener((handleFocus) => {
        const onVisibilityChange = () => handleFocus(document.visibilityState === "visible");
        window.addEventListener("visibilitychange", onVisibilityChange, false);
        return () => {
          window.removeEventListener("visibilitychange", onVisibilityChange);
        };
      });
    };
  }, []);
}
